import { createFileRoute } from '@tanstack/react-router';
import React from 'react';
import { PublicLayout } from '../../components/public-layout.js';
import { SolutionLayout } from '../../components/solution-layout.js';
import { SectionBlock } from '../../components/section-block.js';
import type { CrossSellItem } from '../../components/cross-sell.js';

// ─── cross-sell items ─────────────────────────────────────────────────────────
const CROSS_SELL: CrossSellItem[] = [
  {
    href: '/solutions/pascal-med',
    name: 'PASCAL-Med',
    desc: '의료기관에서 진행하는 정식 디지털 인지 중재 의료기기. PASCAL-Home 사용 환자의 진료 시작점입니다.',
    stage: 'int',
  },
  {
    href: '/solutions/scst',
    name: 'SCST',
    desc: '의료기관에서의 정식 진단보조 검사. PASCAL-Med 진료 전 단계로 자주 활용됩니다.',
    stage: 'dx',
  },
];

// ─── 의무 표시 박스 (README 영업자료 박스 문구 그대로 반영) ──────────────────
function MandatoryBox() {
  return (
    <div
      style={{
        border: '2px solid rgba(245,158,11,0.6)',
        borderRadius: 12,
        padding: '20px 24px',
        background: 'rgba(245,158,11,0.04)',
        marginTop: 28,
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: '#fcd34d',
          marginBottom: 10,
        }}
      >
        ⚠ 중요 안내 (의료법 제27조·의료기기법 준수)
      </div>
      <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.7, margin: 0 }}>
        <strong style={{ color: 'var(--ink)' }}>
          PASCAL-Home은 비의료기기 웰니스 앱이며, 환자 또는 보호자가 BeauBrain에 직접 구독합니다.
        </strong>{' '}
        의료기관은 PASCAL-Home을 진료비로 청구하거나 환자에게 비용을 청구할 수 없으며, 안내만
        제공합니다.
      </p>
      <p style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6, margin: '10px 0 0' }}>
        복지용구 예비급여 포함은 정부 검토 단계이며, 현시점 등재가 보장되지 않습니다. 정책 동향에
        따라 변동될 수 있습니다.
      </p>
    </div>
  );
}

// ─── PAGE ─────────────────────────────────────────────────────────────────────
function Page() {
  return (
    <PublicLayout>
      <SolutionLayout
        stage="int"
        badgeLabel="INT · 인지중재 · 비의료기기 · B2C"
        name="PASCAL-Home"
        tagline="의료기관 안내 후 환자·보호자가 집에서 이어가는 가정 인지 활동 앱."
        domain="비의료기기 · 웰니스 카테고리 · 환자·보호자 직접 구독 (B2C)"
        crossSell={CROSS_SELL}
      >

        {/* ── 의무 안내 박스 (영업 자료 박스 — 원칙2 최우선) ────────────── */}
        <section style={{ padding: '40px 0', borderTop: '1px solid var(--line)' }}>
          <div className="container">
            {/* 비의료기기 안내 배너 */}
            <div
              style={{
                background: 'rgba(0,229,255,0.04)',
                border: '1px solid rgba(0,229,255,0.15)',
                borderRadius: 12,
                padding: '16px 20px',
                display: 'flex',
                gap: 14,
                fontSize: 13,
                color: '#93c5fd',
                lineHeight: 1.6,
              }}
            >
              <div
                style={{
                  width: 22,
                  height: 22,
                  background: 'var(--accent)',
                  color: '#000',
                  borderRadius: '50%',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                i
              </div>
              <div>
                <strong style={{ color: 'var(--ink)', fontWeight: 700 }}>
                  비의료기기 · 가정 인지 활동 안내 앱
                </strong>
                <br />
                PASCAL-Home은 의료기기가 아닌 가정용 인지 활동 앱(웰니스 카테고리)이며, 의료기기로서의
                진단·치료·예방 효능을 표방하지 않습니다. 의료기관에서의 진단·치료는 PASCAL-Med 등 정식
                의료기기를 통해 이루어집니다.
              </div>
            </div>

            {/* 의무 청구 불가 박스 */}
            <MandatoryBox />
          </div>
        </section>

        {/* ── 진료 흐름 카드 ─────────────────────────────────────────────── */}
        <section style={{ padding: '80px 0', borderTop: '1px solid var(--line)' }}>
          <div className="container">
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 24,
              }}
            >
              {/* 메타 정보 */}
              <div
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid var(--line)',
                  borderRadius: 16,
                  padding: 32,
                }}
              >
                <div
                  className="sol-h-badge int"
                  style={{ marginBottom: 16, display: 'inline-block' }}
                >
                  — Home Care
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 20, color: 'var(--ink)' }}>
                  의원 진료 → 가정 인지 활동 → 재방문 시 참고
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {[
                    { val: '의원', label: 'PASCAL-Med 진료' },
                    { val: '가정', label: '일상 인지 활동' },
                    { val: '자동', label: '활동 기록 동기화' },
                    { val: '재방문', label: '변화 참고' },
                  ].map((m) => (
                    <div
                      key={m.val}
                      style={{
                        padding: 16,
                        background: 'rgba(255,255,255,0.02)',
                        border: '1px solid var(--line)',
                        borderRadius: 10,
                        textAlign: 'center',
                      }}
                    >
                      <div
                        className="num"
                        style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b', marginBottom: 6 }}
                      >
                        {m.val}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--ink-3)', fontWeight: 600 }}>{m.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 사용 메타 */}
              <div
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid var(--line)',
                  borderRadius: 16,
                  padding: 32,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 20, color: 'var(--ink)' }}>
                    기본 정보
                  </div>
                  {[
                    { label: '사용 환경', value: '개인 태블릿·스마트폰' },
                    { label: '권장 활동', value: '주 3~5회, 회당 15~20분' },
                    { label: '결제 주체', value: '환자 또는 보호자 직접 (B2C)' },
                    { label: '의료기관 역할', value: '안내만 제공 (청구·정산 관여 없음)' },
                  ].map((m) => (
                    <div
                      key={m.label}
                      style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--line)' }}
                    >
                      <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>{m.label}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{m.value}</span>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 20 }}>
                  <a href="/solutions/pascal-med" className="btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
                    PASCAL-Med 알아보기 →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── 3가지 가치 ─────────────────────────────────────────────────── */}
        <section style={{ padding: '100px 0', borderTop: '1px solid var(--line)' }}>
          <div className="container">
            <SectionBlock label="Why PASCAL-Home" title="의원과 가정을 잇는 인지 활동">
              <div
                style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, marginTop: 56 }}
              >
                {[
                  {
                    n: '01',
                    title: '의원 진료와의 연속성',
                    desc: '의원에서 PASCAL-Med 사용 시 설정된 인지 활동 패키지가 가정 앱과 연동되어, 의원 진료의 흐름을 일상 활동으로 자연스럽게 이어갑니다.',
                  },
                  {
                    n: '02',
                    title: '일상 속 부담 없는 사용',
                    desc: '하루 15~20분의 가벼운 인지 활동. 환자와 가족이 집에서 편안하게 진행하면서 권장 활동 빈도(주 3~5회)를 자기 관리.',
                  },
                  {
                    n: '03',
                    title: '재방문 시 참고 데이터',
                    desc: '가정 활동 기록이 환자 동의 하에 의원 대시보드에 동기화. 재방문 시 담당의가 환자의 일상 활동 추이를 참고할 수 있습니다.',
                  },
                ].map((c) => (
                  <div
                    key={c.n}
                    style={{
                      background: 'rgba(255,255,255,0.02)',
                      border: '1px solid var(--line)',
                      borderRadius: 16,
                      padding: '32px 28px',
                    }}
                  >
                    <div
                      className="num"
                      style={{ fontSize: 11, color: '#f59e0b', fontWeight: 700, letterSpacing: '0.1em', marginBottom: 16 }}
                    >
                      — VALUE {c.n}
                    </div>
                    <div
                      style={{ fontSize: 18, fontWeight: 700, marginBottom: 12, lineHeight: 1.3, color: 'var(--ink)' }}
                    >
                      {c.title}
                    </div>
                    <div style={{ color: 'var(--ink-3)', fontSize: 14, lineHeight: 1.7 }}>{c.desc}</div>
                  </div>
                ))}
              </div>
            </SectionBlock>
          </div>
        </section>

        {/* ── 핵심 기능 ──────────────────────────────────────────────────── */}
        <section style={{ padding: '100px 0', borderTop: '1px solid var(--line)' }}>
          <div className="container">
            <SectionBlock label="Features" title="핵심 기능">
              <div
                style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginTop: 56 }}
              >
                {[
                  { icon: '✓', title: 'PASCAL-Med 연동 맞춤 활동', desc: '의원의 PASCAL-Med 사용 시 설정된 환자별 인지 활동 패키지가 가정 앱과 동기화되어 일관된 흐름을 유지합니다.' },
                  { icon: '⊕', title: '간단한 일상 인터페이스', desc: '고령 환자도 쉽게 사용할 수 있는 간단한 UI. 보호자 도움 없이도 진행 가능.' },
                  { icon: '⌖', title: '활동 기록 자동 동기화', desc: '가정 인지 활동 기록이 환자 동의 하에 의원 대시보드와 자동 동기화되어, 담당의가 일상 활동 추이를 참고할 수 있습니다.' },
                  { icon: '⌕', title: '활동 빈도 알림', desc: '권장 활동 빈도에 맞춰 알림. 환자의 일상 루틴 정착을 지원합니다.' },
                  { icon: '⚐', title: 'PASCAL-Med 결과 연동', desc: '의원의 PASCAL-Med 사용 결과가 동기화되어 가정 인지 활동 난이도가 자연스럽게 조정됩니다.' },
                  { icon: '⌘', title: '보호자 보고서', desc: '주간 활동 보고서가 보호자에게도 자동 전송 (환자 동의 시). 가족이 함께 일상 케어를 살필 수 있습니다.' },
                ].map((f) => (
                  <div
                    key={f.title}
                    style={{
                      background: 'rgba(255,255,255,0.02)',
                      border: '1px solid var(--line)',
                      borderRadius: 14,
                      padding: 24,
                      display: 'flex',
                      gap: 16,
                      alignItems: 'flex-start',
                    }}
                  >
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        background: 'rgba(245,158,11,0.1)',
                        color: '#f59e0b',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: 16,
                        flexShrink: 0,
                      }}
                    >
                      {f.icon}
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, color: 'var(--ink)' }}>
                        {f.title}
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6 }}>{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </SectionBlock>
          </div>
        </section>

        {/* ── FAQ ────────────────────────────────────────────────────────── */}
        <section style={{ padding: '100px 0', borderTop: '1px solid var(--line)' }}>
          <div className="container">
            <SectionBlock label="FAQ" title="자주 묻는 질문">
              <div style={{ maxWidth: 760, margin: '56px auto 0' }}>
                {[
                  {
                    q: 'PASCAL-Home은 의료기기인가요?',
                    a: '아닙니다. PASCAL-Home은 의료기기가 아닌 가정용 인지 활동 앱(웰니스 카테고리)이며, 의료기기로서의 진단·치료·예방 효능을 표방하지 않습니다. 의료기관에서의 진단·치료는 PASCAL-Med 등 정식 의료기기를 통해 이루어집니다.',
                  },
                  {
                    q: 'PASCAL-Home은 어떻게 시작할 수 있나요?',
                    a: '의료기관에서 PASCAL-Med를 사용 중인 환자와 보호자가 의료기관의 안내를 받아 환자 또는 보호자 명의로 직접 BeauBrain에 구독하여 시작합니다.',
                  },
                  {
                    q: '의원에서 비용을 청구하나요?',
                    a: 'PASCAL-Home은 환자 또는 보호자가 직접 BeauBrain에 구독하는 B2C 서비스입니다. 의료기관이 진료비로 청구하는 항목이 아닙니다. 의료기관은 안내만 제공하며 결제·정산에 관여하지 않습니다.',
                  },
                  {
                    q: '의원의 PASCAL-Med와 어떻게 다른가요?',
                    a: 'PASCAL-Med는 의원에서 의사 감독 하에 사용되는 의료기기입니다. PASCAL-Home은 가정에서 환자와 보호자가 일상 인지 활동을 이어갈 수 있도록 돕는 비의료기기 앱입니다. 두 제품의 기록은 환자 동의 하에 동기화되어 담당의가 일상 활동 추이를 참고할 수 있습니다.',
                  },
                  {
                    q: '자가 진단이 가능한가요?',
                    a: '아닙니다. PASCAL-Home은 자가 진단·자가 치료 도구가 아닙니다. 인지 저하가 의심되시면 먼저 의료기관 방문을 권장합니다.',
                  },
                  {
                    q: '사용 결과는 누가 볼 수 있나요?',
                    a: '기본적으로 환자 본인이 확인합니다. 환자 동의 시 담당 의원 대시보드 및 보호자에게도 주간 활동 보고서가 동기화·전송됩니다. 모든 데이터는 개인정보보호법을 준수하여 관리됩니다.',
                  },
                ].map((item) => (
                  <div
                    key={item.q}
                    style={{ borderBottom: '1px solid var(--line)', padding: '20px 0' }}
                  >
                    <div
                      style={{ fontWeight: 600, fontSize: 15, display: 'flex', alignItems: 'center', color: 'var(--ink)' }}
                    >
                      <span className="num" style={{ color: 'var(--accent)', fontWeight: 700, marginRight: 14 }}>
                        Q.
                      </span>
                      {item.q}
                    </div>
                    <div
                      style={{ marginTop: 10, color: 'var(--ink-3)', fontSize: 14, lineHeight: 1.7, paddingLeft: 32 }}
                    >
                      {item.a}
                    </div>
                  </div>
                ))}
              </div>
            </SectionBlock>
          </div>
        </section>

        {/* ── CTA ────────────────────────────────────────────────────────── */}
        <section
          style={{
            padding: '100px 0',
            textAlign: 'center',
            borderTop: '1px solid var(--line)',
            background: 'rgba(255,255,255,0.01)',
          }}
        >
          <div className="container">
            <h2
              style={{
                fontSize: 38,
                fontWeight: 700,
                color: 'var(--ink)',
                marginBottom: 16,
                letterSpacing: '-0.03em',
              }}
            >
              의료기관의 진료를, 가정 인지 활동으로.
            </h2>
            <p style={{ color: 'var(--ink-3)', marginBottom: 32, fontSize: 16, maxWidth: 560, margin: '0 auto 32px' }}>
              PASCAL-Home은 의료기관에서 PASCAL-Med를 사용하는 환자와 보호자가 안내를 받아 직접 구독하는
              비의료기기 가정용 앱입니다.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <a href="/solutions/pascal-med" className="btn-primary btn-lg">
                PASCAL-Med 알아보기 →
              </a>
              <a href="/credit" className="btn-outline btn-lg">
                통합 크레딧 안내
              </a>
            </div>
          </div>
        </section>

      </SolutionLayout>
    </PublicLayout>
  );
}

export const Route = createFileRoute('/solutions/pascal-home')({
  component: Page,
});
